import React, { useRef } from 'react';
import Modal from 'cccisd-modal';
import notification from 'cccisd-notification';
import axios from 'cccisd-axios';
import { Formik, Form, Field, CccisdInput, CccisdDatepicker } from 'cccisd-formik';
import moment from 'moment';
import style from './style.css';

const Boilerplate = window.cccisd.boilerplate;

const OneTimeLink = props => {
    const { sites = [], org = null, assignmentId = null, surveyHandle = null, onSuccess } = props;
    const modalRef = useRef(null);

    const updateSelectedSites = (selectedSites, groupId, isChecked) => {
        if (isChecked) {
            return selectedSites.includes(groupId) ? selectedSites : [...selectedSites, groupId];
        }

        return selectedSites.filter(selectedGroupId => selectedGroupId !== groupId);
    };

    const getParams = (values, siteId) => {
        const siteName = sites.find(s => s.group.groupId === siteId)?.group.label || '';
        const hash = Math.random().toString(36).substring(2, 9);

        return {
            type: 'anon',
            roleHandle: 'respondent',
            hash,
            endpoint: `${hash}anon${siteId}`,
            handle: `anon_${siteId}_${hash}`,
            label: values.collectionDetails,
            description: `org_${org?.group?.groupId || ''}`,
            isOpen: false,
            opensAt: values.opensOn ? moment(values.opensOn).utc().toString() : '',
            closesAt: values.closesOn ? moment(values.closesOn).utc().toString() : '',
            respondentsFromDcwId: null,
            settings: {
                siteId,
                siteName,
                orgId: org?.group?.groupId || null,
                orgName: org?.group?.label || '',
                respondentConfig: 'anonymous',
                type: 'one-time',
                allowRetake: false,
                autoLogout: true,
                autoLogoutTime: 20,
                anonAutoLogoutMessage: `<p>Inactivity Timeout</p><p>We closed your survey session after a period of inactivity.</p>\n                    <p>Sorry! <a href="https://commonjustice.test/d/${hash}" target="_blank" rel="noreferrer noopener">Start a new session</a></p>`,
                branchedCompletionConfig: {},
                chainEmailFromPawn: '',
                chainPhoneFromPawn: '',
                chainLanguageFromPawn: '',
                closeOnTarget: false,
                closedMessage: 'This survey is no longer accepting responses.',
                closedRoster: false,
                completedMessage: 'You have already completed this survey. Thank you!',
                completionMessage: 'Thank you! You have completed the survey.',
                defaultOpenClose: {},
                individualPasscode: false,
                isAssignmentPlanEligible: false,
                isRepeated: false,
                knownAutoLogoutMessage: `<p>Inactivity Timeout</p><p>We closed your survey session after a period of inactivity. Sorry!</p>\n                        <p>You can resume your session from the link you were given, or contact your survey administrator for further assistance.</p>`,
                logoutOnCompletion: false,
                metricToRespondentPropertyMappings: [],
                options: [surveyHandle],
                otherAllowedRoles: ['uberadmin', 'questprojectadmin'],
                passcodeBackground: 'solidColor',
                passcodeBackgroundColor: '#F1F0F5',
                passcodeCompletionButtonText: 'Ok',
                passcodeLabel: 'Your Passcode',
                postCompletionAction: 'message',
                recaptcha: false,
                repeatInterval: '',
                requireLogin: false,
                requirePass: false,
                showReviewButtonOnCompletion: false,
                useEndpoint: true,
            },
            assignmentId,
        };
    };

    const handleSubmit = async values => {
        try {
            if (!Array.isArray(values.selectedSites) || values.selectedSites.length === 0) {
                throw new Error('Select one or more sites before continuing.');
            }

            if (!assignmentId || !org?.group?.groupId) {
                throw new Error('Missing required organization/assignment context. Please reload and try again.');
            }

            await Promise.all(
                values.selectedSites.map(async site => {
                    const params = getParams(values, site);
                    const response = await axios.post(Boilerplate.route('api.assignmentDeployment.store'), params);

                    if (response.data.errors) {
                        throw response.data.errors;
                    }
                })
            );

            if (modalRef.current) {
                modalRef.current.close();
            }

            if (onSuccess) {
                await onSuccess();
            }

            notification({
                message: 'One-Time survey link created successfully.',
                type: 'success',
            });
        } catch (error) {
            let errorMessage = 'An unexpected error occurred.';

            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (Array.isArray(error)) {
                errorMessage = error.join(', ');
            } else if (error && typeof error === 'object') {
                errorMessage = Object.values(error).join(', ');
            }

            notification({ message: errorMessage, type: 'danger' });
            throw error;
        }

        return values;
    };

    const validate = values => {
        let errors = {};

        if (!values.collectionDetails) {
            errors.collectionDetails = 'Collection Details is required';
        }

        if (!values.opensOn) {
            errors.opensOn = 'Opens On date is required';
        }

        if (values.opensOn && new Date(values.opensOn) < new Date()) {
            errors.opensOn = 'Opens On date cannot be in the past.';
        }

        if (values.closesOn && new Date(values.closesOn) < new Date()) {
            errors.closesOn = 'Closes On date cannot be in the past.';
        }

        if (values.closesOn && values.opensOn === values.closesOn) {
            errors.opensOn = 'Opens On date and Closes On date cannot be the same.';
            errors.closesOn = 'Opens On date and Closes On date cannot be the same.';
        }

        if (values.opensOn && values.closesOn && new Date(values.closesOn) < new Date(values.opensOn)) {
            errors.closesOn = 'Closes On date cannot be before Opens On date.';
        }

        if (!Array.isArray(values.selectedSites) || values.selectedSites.length === 0) {
            errors.selectedSites = 'Select one or more sites before continuing.';
        }

        return errors;
    };

    return (
        <Modal
            ref={modalRef}
            title="Create One-Time Survey Link"
            trigger={
                <button type="button" className="btn btn-primary">
                    Create One-Time Survey Link
                </button>
            }
        >
            <Formik
                initialValues={{
                    collectionDetails: '',
                    opensOn: '',
                    closesOn: '',
                    selectedSites: [],
                }}
                onSubmit={handleSubmit}
                validate={validate}
                render={({ values, setFieldValue, errors, submitCount }) => (
                    <Form>
                        <Field
                            name="collectionDetails"
                            component={CccisdInput}
                            label={<span className={style.required}>Collection Details</span>}
                            type="text"
                        />
                        <div className="row">
                            <div className="col-sm-6">
                                <Field
                                    name="opensOn"
                                    component={CccisdDatepicker}
                                    placeholder="Pick date & time"
                                    showTimeSelect
                                    label={<span className={style.required}>Opens On</span>}
                                />
                            </div>
                            <div className="col-sm-6">
                                <Field
                                    name="closesOn"
                                    component={CccisdDatepicker}
                                    placeholder="Pick date & time"
                                    showTimeSelect
                                    label="Closes On"
                                />
                            </div>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                            <label>
                                <strong>
                                    <span className={style.required}>Select Site(s)</span>
                                </strong>
                            </label>
                            <table className="table table-bordered table-striped" style={{ marginBottom: 0 }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '70px' }}>Select</th>
                                        <th>Site Name</th>
                                        <th>Site Type</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sites.length > 0 ? (
                                        sites.map(site => (
                                            <tr key={site.group.groupId}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={values.selectedSites.includes(site.group.groupId)}
                                                        onChange={event =>
                                                            setFieldValue(
                                                                'selectedSites',
                                                                updateSelectedSites(
                                                                    values.selectedSites,
                                                                    site.group.groupId,
                                                                    event.target.checked
                                                                )
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td>{site.group.label}</td>
                                                <td>{site.fields.siteType}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3">No sites available.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            {submitCount > 0 && errors.selectedSites && (
                                <div className="text-danger" style={{ marginTop: '8px' }}>
                                    {errors.selectedSites}
                                </div>
                            )}
                        </div>
                        <div className="d-flex justify-content-between">
                            <button
                                className="btn btn-secondary"
                                type="button"
                                onClick={() => modalRef.current && modalRef.current.close()}
                            >
                                Cancel
                            </button>
                            <button className="btn btn-primary" type="submit">
                                Submit
                            </button>
                        </div>
                    </Form>
                )}
            />
        </Modal>
    );
};

export default OneTimeLink;
